import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Segmented } from '@/components/ui/segmented';
import { StatusChip } from '@/components/ui/status-chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';
import {
  getSettings, updateSettings, changePassword,
  adminGetUsers, adminCreateUser, adminUpdateUser, adminResetPassword,
  adminGetDoctors, adminUpdateDoctor,
  adminGetClinic, adminUpdateClinic, adminGetLoginLink, getClinicHours, adminSaveClinicHours,
  type ClinicHours, type WeekDay,
  adminGetInvoiceSettings, adminUpdateInvoiceSettings,
} from '@/lib/api';
import BillingServicesTab from '@/components/Billing/BillingServicesTab';
import GettingStartedCard from '@/components/GettingStartedCard';
import { useSearchParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { loginLinkUrl } from '@/lib/clinicBrand';
import { APP_NAME } from '@/components/auth/AuthLayout';
import BrandingImageField from '@/components/BrandingImageField';
import { getBrandingImages, type BrandingImages } from '@/lib/brandingApi';
import { setClinicHoursCache, WEEK_DAYS } from '@/hooks/useClinicHours';
import {
  User, Lock, Building2, Users, Stethoscope, Receipt,
  CheckCircle, AlertTriangle, Plus, Pencil, KeyRound,
  ToggleLeft, ToggleRight, Save, RefreshCw, ShieldCheck, FileEdit, Check, Copy, Download, Clock, X,
} from 'lucide-react';
import {
  getPermissionRoleTemplates, getUserPermissions, updateUserPermissions,
} from '@/lib/api';
import { usePermission } from '@/hooks/usePermission';
import { CustomFieldConfig, PrescriptionLayoutConfig } from '@/types/prescription';
import { DEFAULT_PRESCRIPTION_LAYOUT, clearPrescriptionLayoutCache } from '@/hooks/usePrescriptionLayout';

// ─── Toast helper ─────────────────────────────────────────────────────────────

function useNotify() {
  const [note, setNote] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);
  return { note, ok: (msg: string) => setNote({ type: 'success', msg }), err: (msg: string) => setNote({ type: 'error', msg }) };
}

function Notify({ note }: { note: { type: 'success' | 'error'; msg: string } | null }) {
  if (!note) return null;
  return (
    <Alert className={cn('mb-4', note.type === 'success' ? 'border-success/30 bg-success/5' : 'border-destructive/30 bg-destructive/5')}>
      {note.type === 'success'
        ? <CheckCircle className="h-4 w-4 text-success" />
        : <AlertTriangle className="h-4 w-4 text-destructive" />}
      <AlertDescription className={note.type === 'success' ? 'text-success' : 'text-destructive'}>{note.msg}</AlertDescription>
    </Alert>
  );
}

// ─── Shared field ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, desc, children }: { label: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 border-b border-border last:border-0">
      <div className="min-w-0"><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
      {children}
    </div>
  );
}

// ─── 1. Profile tab ───────────────────────────────────────────────────────────

function ProfileTab() {
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ name: '', email: '', phone: '', specialty: 'General Medicine', license: '', signature: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id)
      .then(s => {
        if (s?.profile) setForm({
          name:      s.profile.name      || currentUser.name || '',
          email:     s.profile.email     || currentUser.email || '',
          phone:     s.profile.phone     || '',
          specialty: s.profile.specialty || 'General Medicine',
          license:   s.profile.license   || '',
          signature: s.profile.signature || '',
        });
      }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        profile_name: form.name, profile_email: form.email, profile_phone: form.phone,
        profile_specialty: form.specialty, profile_license: form.license, profile_signature: form.signature,
      });
      ok('Profile saved');
    } catch { err('Failed to save profile'); }
    finally { setSaving(false); }
  };

  const s = (k: keyof typeof form) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="max-w-xl space-y-4">
      <Notify note={note} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full Name"><Input value={form.name} onChange={e => s('name')(e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={e => s('email')(e.target.value)} /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={e => s('phone')(e.target.value)} /></Field>
        <Field label="Specialty">
          <Select value={form.specialty} onValueChange={s('specialty')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['General Practitioner','General Medicine','Cardiology','Dermatology','Pediatrics','Orthopedics','Gynecology','ENT','Neurology','Psychiatry','Ophthalmology'].map(sp => (
                <SelectItem key={sp} value={sp}>{sp}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Medical License No."><Input value={form.license} onChange={e => s('license')(e.target.value)} className="col-span-2" /></Field>
      </div>
      <Field label="Signature (appears on prescriptions)">
        <Textarea value={form.signature} onChange={e => s('signature')(e.target.value)} rows={2} />
      </Field>
      {can('settings:write') && (
        <Button onClick={save} disabled={saving} size="sm">
          {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save Profile
        </Button>
      )}
      {currentUser?.doctor_id && <SignatureImageSection doctorId={currentUser.doctor_id} />}
    </div>
  );
}

/** The doctor's handwritten signature, printed above the signature line on their prescriptions */
function SignatureImageSection({ doctorId }: { doctorId: string }) {
  const [image, setImage] = useState<string | null | undefined>(undefined);
  const load = useCallback(() => {
    getBrandingImages(doctorId).then(r => setImage(r?.signature ?? null)).catch(() => setImage(null));
  }, [doctorId]);
  useEffect(load, [load]);
  if (image === undefined) return null;
  return (
    <section className="pt-6 mt-2 border-t border-border">
      <BrandingImageField
        kind="signature"
        label="Signature image"
        description="Sign on plain white paper and take a clear photo, or upload a scan. It prints above your name on prescriptions and discharge summaries."
        image={image}
        onChanged={load}
      />
    </section>
  );
}

// ─── 2. Password tab ──────────────────────────────────────────────────────────

function PasswordTab() {
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.next !== form.confirm) { err('New passwords do not match'); return; }
    if (form.next.length < 6) { err('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await changePassword(form.current, form.next);
      ok('Password changed'); setForm({ current: '', next: '', confirm: '' });
    } catch (e: any) { err(e?.message || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xs space-y-4">
      <Notify note={note} />
      <Field label="Current Password"><PasswordInput value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} /></Field>
      <Field label="New Password"><PasswordInput value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} /></Field>
      <Field label="Confirm New Password"><PasswordInput value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} /></Field>
      <div className="flex items-center justify-end">
        <Button onClick={save} disabled={saving || !form.current || !form.next} size="sm">
          {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
          Change Password
        </Button>
      </div>
    </div>
  );
}

// ─── 3. Preferences tab ───────────────────────────────────────────────────────

function PreferencesTab() {
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const { theme, setTheme } = useTheme();
  const { note, ok, err } = useNotify();
  const [autoSave, setAutoSave] = useState(true);
  const [notifications, setNotifications] = useState(true);
  const [printFormat, setPrintFormat] = useState<'a4' | 'letter' | 'compact'>('a4');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id).then(s => {
      if (!s) return;
      if (s.preferences?.autoSave   !== undefined) setAutoSave(!!s.preferences.autoSave);
      if (s.preferences?.notifications !== undefined) setNotifications(!!s.preferences.notifications);
      if (s.preferences?.printFormat)  setPrintFormat(s.preferences.printFormat);
    }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        theme, language: 'en', notifications, auto_save: autoSave, print_format: printFormat,
      });
      clearPrescriptionLayoutCache();
      ok('Preferences saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-2">
      <Notify note={note} />
      <Row label="Theme" desc="Application colour scheme">
        <Select value={theme} onValueChange={v => setTheme(v as any)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="light">Light</SelectItem>
            <SelectItem value="dark">Dark</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Print Format" desc="Default paper size for prescriptions">
        <Select value={printFormat} onValueChange={v => setPrintFormat(v as any)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="a4">A4</SelectItem>
            <SelectItem value="letter">Letter</SelectItem>
            <SelectItem value="compact">Compact</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Auto-save" desc="Keep an unsaved prescription on this device and bring it back when you return">
        <Switch checked={autoSave} onCheckedChange={setAutoSave} />
      </Row>
      <Row label="Notifications" desc="In-app reminders and alerts">
        <Switch checked={notifications} onCheckedChange={setNotifications} />
      </Row>
      {can('settings:write') && (
        <Button onClick={save} disabled={saving} size="sm" className="mt-2">
          {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save Preferences
        </Button>
      )}
    </div>
  );
}

// ─── 3b. Prescription Layout tab ──────────────────────────────────────────────

const HIDEABLE_PRESCRIPTION_SECTIONS: { id: string; label: string; desc: string }[] = [
  { id: 'vitals',         label: 'Vital Signs',    desc: 'BP, weight, temperature, etc.' },
  { id: 'examination',    label: 'Examination',    desc: 'Pallor, icterus, edema and findings by system' },
  { id: 'investigations', label: 'Investigations', desc: 'Lab tests and imaging' },
  { id: 'advice',         label: 'Advice',         desc: 'General advice to patient' },
  { id: 'notes',          label: 'Quick Notes',    desc: 'Free-form notes' },
  { id: 'next-visit',     label: 'Next Visit',     desc: 'Follow-up date and reminders' },
];

// Where a custom field sits on the Rx pad and the printout
const CUSTOM_FIELD_POSITIONS: { id: string; label: string }[] = [
  { id: 'complaints',     label: 'After Complaints' },
  { id: 'vitals',         label: 'After Vitals' },
  { id: 'examination',    label: 'After Examination' },
  { id: 'diagnosis',      label: 'After Diagnosis' },
  { id: 'medicines',      label: 'After Medicines' },
  { id: 'investigations', label: 'After Tests' },
  { id: 'advice',         label: 'After Advice' },
  { id: 'notes',          label: 'After Quick notes' },
  { id: 'next-visit',     label: 'After Follow-up' },
  { id: 'end',            label: 'At the end' },
];

function PrescriptionLayoutTab() {
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const { note, ok, err } = useNotify();
  const [layout, setLayout] = useState<PrescriptionLayoutConfig>(DEFAULT_PRESCRIPTION_LAYOUT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    getSettings(currentUser.doctor_id, currentUser.clinic_id).then(s => {
      if (s?.prescriptionLayout) setLayout(s.prescriptionLayout);
    }).catch(() => {});
  }, [currentUser?.doctor_id]);

  const toggleSection = (id: string, visible: boolean) => {
    setLayout(prev => ({
      ...prev,
      hiddenSections: visible
        ? prev.hiddenSections.filter(s => s !== id)
        : [...prev.hiddenSections, id],
    }));
  };

  const updateCustomField = (key: string, changes: Partial<CustomFieldConfig>) => {
    setLayout(prev => ({
      ...prev,
      customFields: prev.customFields.map(cf => cf.key === key ? { ...cf, ...changes } : cf),
    }));
  };

  // Typing a label switches the field on; clearing it switches it off. The switch still works on its own.
  const setCustomLabel = (cf: CustomFieldConfig, label: string) => {
    const had = !!cf.label.trim(), has = !!label.trim();
    updateCustomField(cf.key, { label, ...(had !== has && { enabled: has }) });
  };

  const save = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setSaving(true);
    try {
      await updateSettings(currentUser.doctor_id, currentUser.clinic_id, {
        doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id,
        prescription_layout: layout,
      });
      clearPrescriptionLayoutCache();
      ok('Prescription layout saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-6">
      <Notify note={note} />

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Sections</p>
        <p className="text-xs text-muted-foreground mb-2">Hide sections you don't use. Complaints, Diagnosis, and Medications are always shown.</p>
        {HIDEABLE_PRESCRIPTION_SECTIONS.map(s => (
          <Row key={s.id} label={s.label} desc={s.desc}>
            <Switch
              checked={!layout.hiddenSections.includes(s.id)}
              onCheckedChange={(checked) => toggleSection(s.id, checked)}
            />
          </Row>
        ))}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Custom Fields</p>
        <p className="text-xs text-muted-foreground mb-2">Add up to 3 extra free-text fields with your own labels.</p>
        {layout.customFields.map((cf, idx) => (
          <div key={cf.key} className="flex items-center gap-2 py-2 border-b last:border-0">
            <Switch
              checked={cf.enabled}
              onCheckedChange={(checked) => updateCustomField(cf.key, { enabled: checked })}
            />
            <div className="flex-1 min-w-0 space-y-1.5">
              <Input
                value={cf.label}
                onChange={(e) => setCustomLabel(cf, e.target.value)}
                placeholder={`Custom field ${idx + 1} label`}
                className="h-8 text-sm"
                maxLength={50}
              />
              {cf.enabled && (
                <Select value={cf.after ?? 'end'} onValueChange={v => updateCustomField(cf.key, { after: v === 'end' ? undefined : v })}>
                  <SelectTrigger className="h-8 text-xs" aria-label={`Position of custom field ${idx + 1}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CUSTOM_FIELD_POSITIONS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
      </div>

      {can('settings:write') && (
        <Button onClick={save} disabled={saving} size="sm">
          {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save Layout
        </Button>
      )}
    </div>
  );
}

// ─── 4. Clinic Info tab (admin) ───────────────────────────────────────────────

function ClinicTab() {
  const { note, ok, err } = useNotify();
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '', gstin: '', visit_auto_close_hours: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetClinic().then(c => {
      if (c) setForm({
        name: c.name || '', address: c.address || '', phone: c.phone || '', email: c.email || '', gstin: c.gstin || '',
        visit_auto_close_hours: c.visit_auto_close_hours != null ? String(c.visit_auto_close_hours) : '',
      });
    }).catch(() => {});
  }, []);

  const s = (k: keyof typeof form) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { err('Clinic name is required'); return; }
    const hours = form.visit_auto_close_hours.trim();
    if (hours && !(/^\d+$/.test(hours) && +hours >= 1 && +hours <= 72)) { err('Close unfinished visits after 1 to 72 hours'); return; }
    setSaving(true);
    try {
      await adminUpdateClinic({ ...form, visit_auto_close_hours: hours ? Number(hours) : null });
      ok('Clinic info saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl space-y-4">
      <Notify note={note} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-2"><Field label="Clinic Name *"><Input value={form.name} onChange={e => s('name')(e.target.value)} /></Field></div>
        <Field label="Phone"><Input value={form.phone} onChange={e => s('phone')(e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={e => s('email')(e.target.value)} /></Field>
        <Field label="GSTIN"><Input value={form.gstin} onChange={e => s('gstin')(e.target.value)} placeholder="22AAAAA0000A1Z5" /></Field>
      </div>
      <Field label="Address">
        <Textarea value={form.address} onChange={e => s('address')(e.target.value)} rows={3} />
      </Field>
      <Row label="Close unfinished visits after" desc="Visits with no activity for this long are closed and marked 'Not completed', so the next day's queue starts clean. Leave empty for 12 hours.">
        <div className="flex items-center gap-2">
          <Input id="visit-auto-close-hours" inputMode="numeric" className="w-20" placeholder="12"
            value={form.visit_auto_close_hours} onChange={e => s('visit_auto_close_hours')(e.target.value)} />
          <span className="text-sm text-muted-foreground">hours</span>
        </div>
      </Row>
      <Button onClick={save} disabled={saving} size="sm">
        {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
        Save Clinic Info
      </Button>
      <ClinicImagesSection />
      <LoginLinkSection />
    </div>
  );
}

// ─── Clinic hours (admin) ─────────────────────────────────────────────────────

const DAY_LABEL: Record<WeekDay, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
  friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};
const MAX_SESSIONS = 3;
// A typical clinic week to start from: a morning and an evening session, Sunday off
const TYPICAL_WEEK = (): ClinicHours => Object.fromEntries(WEEK_DAYS.map(d => [d,
  d === 'sunday' ? [] : [{ start: '10:00', end: '13:00' }, { start: '17:00', end: '21:00' }],
])) as ClinicHours;
const CLOSED_WEEK = (): ClinicHours => Object.fromEntries(WEEK_DAYS.map(d => [d, []])) as ClinicHours;
const pad2 = (n: number) => String(n).padStart(2, '0');

/** What's wrong with a day's sessions, if anything (the server checks the same) */
function sessionsError(sessions: ClinicHours[WeekDay]) {
  const sorted = [...sessions].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 0; i < sorted.length; i++) {
    const t = sorted[i];
    if (!t.start || !t.end) return 'Enter a start and end time';
    if (t.end <= t.start) return 'A session must end after it starts';
    if (i > 0 && t.start < sorted[i - 1].end) return 'Sessions overlap';
  }
  return null;
}

function ClinicHoursTab() {
  const { currentUser } = useAuth();
  const { note, ok, err } = useNotify();
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState<ClinicHours | null>(null);
  // null: hours not set and not being edited
  const [week, setWeek] = useState<ClinicHours | null>(null);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    getClinicHours().then(r => { setSaved(r?.hours ?? null); setWeek(r?.hours ?? null); })
      .catch(() => err('Could not load clinic hours'))
      .finally(() => setLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setDay = (day: WeekDay, sessions: ClinicHours[WeekDay]) => setWeek(w => w && { ...w, [day]: sessions });
  const errors = week
    ? Object.fromEntries(WEEK_DAYS.map(d => [d, sessionsError(week[d])])) as Record<WeekDay, string | null>
    : null;
  const dirty = JSON.stringify(week) !== JSON.stringify(saved);

  const save = async (hours: ClinicHours) => {
    setSaving(true);
    try {
      const r = await adminSaveClinicHours(hours);
      const next = r?.hours ?? null;
      setSaved(next); setWeek(next); setTried(false);
      if (currentUser?.clinic_id) setClinicHoursCache(currentUser.clinic_id, next);
      ok(next ? 'Clinic hours saved' : 'Clinic hours cleared');
    } catch (e: any) { err(e?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const submit = () => {
    if (!week || !errors) return;
    setTried(true);
    if (WEEK_DAYS.some(d => errors[d])) { err('Fix the hours marked below'); return; }
    save(week);
  };
  // The next session starts an hour after the last one ends and runs three hours
  const addSession = (day: WeekDay) => {
    const sessions = week?.[day] ?? [];
    const last = sessions[sessions.length - 1];
    const h = Math.min(22, Number((last?.end || '16:00').slice(0, 2)) + 1);
    setDay(day, [...sessions, { start: `${pad2(h)}:00`, end: `${pad2(Math.min(23, h + 3))}:00` }]);
  };
  const copyMonday = () => setWeek(w => w && Object.fromEntries(WEEK_DAYS.map(d =>
    [d, d === 'monday' || d === 'sunday' ? w[d] : w.monday.map(t => ({ ...t }))])) as ClinicHours);

  if (!loaded) return null;

  return (
    <div className="max-w-2xl space-y-4">
      <Notify note={note} />
      <p className="text-sm text-muted-foreground">
        Booking offers time slots only while the clinic is open, and the day calendar follows these hours.
        A patient can still be booked at another time with "Other time".
      </p>

      {!week ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-4 space-y-3">
          <p className="text-sm">
            <span className="font-medium">Hours not set.</span>{' '}
            <span className="text-muted-foreground">Booking offers every slot from 8 AM to 10 PM.</span>
          </p>
          <Button size="sm" onClick={() => setWeek(TYPICAL_WEEK())} className="gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Set clinic hours
          </Button>
        </div>
      ) : (
        <>
          <ul aria-label="Clinic hours" className="rounded-lg border border-border bg-card divide-y divide-border">
            {WEEK_DAYS.map(day => {
              const sessions = week[day];
              const open = sessions.length > 0;
              const error = tried ? errors?.[day] : null;
              return (
                <li key={day} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 px-4 py-3">
                  <div className="flex items-center gap-3 sm:w-36 sm:shrink-0 sm:h-9">
                    <Switch
                      id={`open-${day}`} checked={open}
                      onCheckedChange={v => setDay(day, v ? [{ start: '10:00', end: '13:00' }] : [])}
                    />
                    <Label htmlFor={`open-${day}`} className="text-sm font-medium">{DAY_LABEL[day]}</Label>
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    {!open ? (
                      <p className="text-sm text-muted-foreground sm:leading-9">Closed</p>
                    ) : (
                      <>
                        {sessions.map((t, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input type="time" value={t.start} aria-label={`${DAY_LABEL[day]}, session ${i + 1} opens`}
                              onChange={e => setDay(day, sessions.map((x, j) => j === i ? { ...x, start: e.target.value } : x))}
                              className="h-9 w-32 text-sm" />
                            <span className="text-sm text-muted-foreground">to</span>
                            <Input type="time" value={t.end} aria-label={`${DAY_LABEL[day]}, session ${i + 1} closes`}
                              onChange={e => setDay(day, sessions.map((x, j) => j === i ? { ...x, end: e.target.value } : x))}
                              className="h-9 w-32 text-sm" />
                            {sessions.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 touch:h-11 touch:w-11 shrink-0"
                                aria-label={`Remove ${DAY_LABEL[day]} session ${i + 1}`}
                                onClick={() => setDay(day, sessions.filter((_, j) => j !== i))}>
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        {error && <p className="text-xs text-destructive">{error}</p>}
                        {sessions.length < MAX_SESSIONS && (
                          <button type="button" onClick={() => addSession(day)}
                            className="text-[13px] font-medium text-primary hover:underline underline-offset-2">
                            + Add session
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={submit} disabled={saving || !dirty} size="sm">
              {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save hours
            </Button>
            <Button variant="outline" size="sm" disabled={saving} onClick={copyMonday}>
              Copy Monday to Tue – Sat
            </Button>
            {dirty && (
              <Button variant="ghost" size="sm" disabled={saving} onClick={() => { setWeek(saved); setTried(false); }}>
                {saved ? 'Undo changes' : 'Cancel'}
              </Button>
            )}
            {saved && !dirty && (
              <Button variant="ghost" size="sm" disabled={saving} className="ml-auto text-muted-foreground"
                onClick={() => save(CLOSED_WEEK())}>
                Clear hours
              </Button>
            )}
          </div>
          {WEEK_DAYS.every(d => week[d].length === 0) && (
            <p className="text-xs text-muted-foreground">
              Every day is closed: saving clears the hours, and booking offers the whole day again.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The clinic's logo and letterhead banner, for printed prescriptions, bills and its sign-in page */
function ClinicImagesSection() {
  const [images, setImages] = useState<BrandingImages | null>(null);
  const load = useCallback(() => { getBrandingImages().then(setImages).catch(() => {}); }, []);
  useEffect(load, [load]);
  if (!images) return null;
  return (
    <section className="pt-6 mt-2 border-t border-border space-y-5">
      <div>
        <h3 className="text-sm font-semibold">Logo and letterhead</h3>
        <p className="text-xs text-muted-foreground">Printed on prescriptions, bills and discharge summaries.</p>
      </div>
      <BrandingImageField
        kind="logo"
        label="Logo"
        description="Square or round works best. Shown beside your clinic's name on prints and on your clinic's sign-in page."
        image={images.logo}
        onChanged={load}
      />
      <BrandingImageField
        kind="letterhead"
        label="Letterhead banner"
        description="Your full letterhead as one wide image, A4 width. When set, prints use it in place of the typed header (changeable in the print window)."
        image={images.letterhead}
        onChanged={load}
      />
    </section>
  );
}

/** The clinic's own sign-in page: the link to share with staff and its QR code for the front desk.
 *  The platform admin assigns and changes the link; the clinic only sees and shares it. */
function LoginLinkSection() {
  const [slug, setSlug] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = React.useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    adminGetLoginLink().then(r => {
      if (!r) return;
      setSlug(r.slug);
      setLoaded(true);
    }).catch(() => {});
  }, []);

  const url = slug ? loginLinkUrl(slug) : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the link is still selectable */ }
  };

  const downloadQr = () => {
    const canvas = qrRef.current;
    if (!canvas || !slug) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `login-qr-${slug}.png`;
    a.click();
  };

  if (!loaded) return null;

  return (
    <section className="pt-6 mt-2 border-t border-border space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Login link</h3>
        <p className="text-xs text-muted-foreground">
          Your clinic's own sign-in page, with your clinic's name on it. Share it with staff or put the QR code at the front desk.
        </p>
      </div>

      {slug ? (
        <div className="flex flex-wrap items-start gap-4">
          <div className="rounded-md border border-border bg-white p-2">
            <QRCodeCanvas ref={qrRef} value={url} size={112} marginSize={1} aria-label="QR code for the login link" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-mono text-sm break-all select-all">{url}</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copy} className="gap-1.5">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadQr} className="gap-1.5">
                <Download className="w-3.5 h-3.5" />
                Download QR
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">To change this link, contact {APP_NAME} support.</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground rounded-md border border-dashed border-border px-3 py-2.5">
          Your clinic doesn't have a login link yet. Contact {APP_NAME} support to set one up.
        </p>
      )}
    </section>
  );
}

// Shown once after creating a user or resetting a password with an auto-generated password
function TempPasswordDialog({ value, onClose }: { value: { email: string; password: string } | null; onClose: () => void }) {
  return (
    <Dialog open={!!value} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xs">
        <DialogHeader><DialogTitle>Temporary Password</DialogTitle></DialogHeader>
        <div className="py-2 space-y-2">
          <p className="text-sm text-muted-foreground">
            Share this with <span className="font-medium text-foreground">{value?.email}</span>. It won't be shown again.
          </p>
          <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm">
            {value?.password}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── 5. Staff (Users) tab (admin) ─────────────────────────────────────────────

const ROLES = ['doctor', 'staff', 'admin'] as const;
type Role = typeof ROLES[number];

const ROLE_LABEL: Record<string, string> = { admin: 'Admin', doctor: 'Doctor', staff: 'Staff' };

function StaffTab() {
  const { currentUser } = useAuth();
  const { isOwner } = usePermission();
  const { note, ok, err } = useNotify();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add user modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'staff' as Role });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit modal
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'staff' as Role, is_owner: false });
  const [editSaving, setEditSaving] = useState(false);

  // Reset pw modal
  const [resetUser, setResetUser] = useState<any | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Generated temp password (shown once after create/reset)
  const [generatedPw, setGeneratedPw] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const u = await adminGetUsers();
      setUsers(u ?? []);
    } catch { err('Failed to load staff'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setAddError('');
    if (!addForm.name || !addForm.email) { setAddError('Name and email are required'); return; }
    if (addForm.password && addForm.password.length < 6) { setAddError('Password must be at least 6 characters'); return; }
    setAddSaving(true);
    try {
      const created = await adminCreateUser(addForm.password ? addForm : { ...addForm, password: undefined });
      ok('User created'); setAddOpen(false);
      if (created?.temp_password) setGeneratedPw({ email: addForm.email, password: created.temp_password });
      setAddForm({ name: '', email: '', password: '', role: 'staff' }); load();
    } catch (e: any) { setAddError(e?.message || 'Failed to create user'); }
    finally { setAddSaving(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const data: any = { name: editForm.name };
      if (isOwner) data.is_owner = editForm.is_owner;
      await adminUpdateUser(editUser.id, data);
      ok('User updated'); setEditUser(null); load();
    } catch (e: any) { err(e?.message || 'Failed to update user'); }
    finally { setEditSaving(false); }
  };

  const toggleActive = async (u: any) => {
    try {
      await adminUpdateUser(u.id, { is_active: u.is_active ? 0 : 1 });
      ok(`User ${u.is_active ? 'deactivated' : 'activated'}`); load();
    } catch { err('Failed'); }
  };

  const handleReset = async () => {
    if (!resetUser) return;
    setResetSaving(true);
    try {
      const result = await adminResetPassword(resetUser.id, resetPw || undefined);
      ok('Password reset');
      if (result?.temp_password) setGeneratedPw({ email: resetUser.email, password: result.temp_password });
      setResetUser(null); setResetPw('');
    } catch (e: any) { err(e?.message || 'Failed'); }
    finally { setResetSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Notify note={note} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => { setAddForm({ name: '', email: '', password: '', role: 'staff' }); setAddError(''); setAddOpen(true); }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add User
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="h-8 border-b border-border bg-muted/40">
                {['Name', 'Email', 'Role', 'Linked Doctor', 'Status', ''].map(h => (
                  <th key={h} scope="col" className="px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={cn('h-10 border-b border-divider last:border-0', !u.is_active && 'text-muted-foreground')}>
                  <td className={cn('px-3 py-1 font-medium whitespace-nowrap', u.is_active && 'text-foreground')}>{u.name}</td>
                  <td className="px-3 py-1 text-muted-foreground">{u.email}</td>
                  <td className="px-3 py-1 whitespace-nowrap">
                    <StatusChip tone="neutral">{ROLE_LABEL[u.role] ?? u.role}</StatusChip>
                    {!!u.is_owner && <StatusChip tone="warning" className="ml-1">Owner</StatusChip>}
                  </td>
                  <td className="px-3 py-1 text-muted-foreground whitespace-nowrap">
                    {u.doctor_name || (!u.doctor_id && u.role === 'doctor'
                      ? <span title="No doctor profile linked"><StatusChip tone="alert">Unlinked</StatusChip></span>
                      : u.doctor_id ? '' : '—')}
                  </td>
                  <td className="px-3 py-1">
                    <StatusChip tone={u.is_active ? 'success' : 'neutral'}>{u.is_active ? 'Active' : 'Inactive'}</StatusChip>
                  </td>
                  <td className="px-3 py-1">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit" onClick={() => { setEditUser(u); setEditForm({ name: u.name, role: u.role, is_owner: !!u.is_owner }); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Reset password" aria-label={`Reset password for ${u.name}`} onClick={() => { setResetUser(u); setResetPw(''); }}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title={
                          u.id === currentUser?.id
                            ? "Cannot deactivate your own account"
                            : u.is_owner
                            ? "Cannot deactivate the owner/admin"
                            : u.is_active ? 'Deactivate' : 'Activate'
                        }
                        onClick={() => toggleActive(u)}
                        disabled={u.is_owner || u.id === currentUser?.id}
                      >
                        {u.is_active ? <ToggleRight className={cn("w-3.5 h-3.5", u.is_owner || u.id === currentUser?.id ? "text-muted-foreground" : "text-success")} /> : <ToggleLeft className="w-3.5 h-3.5 text-muted-foreground" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name *"><Input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Email *"><Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></Field>
            <Field label="Password (optional)">
              <PasswordInput value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">If left blank, a temporary password is generated and shown once. The user must change it on first login.</p>
            </Field>
            <Field label="Role">
              <Select value={addForm.role} onValueChange={v => setAddForm(f => ({ ...f, role: v as Role }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {addForm.role === 'doctor' && (
              <p className="text-xs text-muted-foreground">A doctor profile will be created automatically for this user.</p>
            )}
            {addError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{addError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addSaving}>{addSaving ? 'Creating…' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editUser} onOpenChange={v => { if (!v) setEditUser(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit User — {editUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name"><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Role">
              <Input value={editForm.role} className="capitalize" disabled />
              <p className="text-xs text-muted-foreground mt-1">Role cannot be changed after creation.</p>
            </Field>
            {editForm.role === 'doctor' && !editUser?.doctor_id && (
              <p className="text-xs text-muted-foreground">A doctor profile will be created automatically for this user.</p>
            )}
            {isOwner && (
              <Row label="Owner" desc="Owners can manage staff permissions and other owners">
                <Switch
                  checked={editForm.is_owner}
                  disabled={editUser?.id === currentUser?.id}
                  onCheckedChange={v => setEditForm(f => ({ ...f, is_owner: v }))}
                />
              </Row>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetUser} onOpenChange={v => { if (!v) setResetUser(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Reset Password — {resetUser?.name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Field label="New Password (optional)">
              <PasswordInput value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">The user must change this password on next login.</p>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)} disabled={resetSaving}>Cancel</Button>
            <Button onClick={handleReset} disabled={resetSaving || (resetPw.length > 0 && resetPw.length < 6)}>{resetSaving ? 'Resetting…' : 'Reset'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated temp password */}
      <TempPasswordDialog value={generatedPw} onClose={() => setGeneratedPw(null)} />
    </div>
  );
}

// ─── 6. Doctors tab (admin) ───────────────────────────────────────────────────

function DoctorsTab() {
  const { currentUser } = useAuth();
  const { note, ok, err } = useNotify();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState<any | null>(null);
  const [form, setForm] = useState({ name: '', specialty: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setDoctors((await adminGetDoctors()) ?? []); }
    catch { err('Failed to load doctors'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (d: any) => { setEditDoc(d); setForm({ name: d.name, specialty: d.specialty || '', phone: d.phone || '', email: d.email || '' }); setFormError(''); };

  const [modalOpen, setModalOpen] = useState(false);

  // Add doctor = create a login with the Doctor role (allowed on every plan);
  // the backend creates the linked doctor profile.
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [generatedPw, setGeneratedPw] = useState<{ email: string; password: string } | null>(null);

  const openAdd = () => { setAddForm({ name: '', email: '', password: '' }); setAddError(''); setAddOpen(true); };

  // Reset a doctor's login password (allowed on every plan)
  const [resetDoc, setResetDoc] = useState<any | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  const handleReset = async () => {
    if (!resetDoc) return;
    setResetSaving(true);
    try {
      const result = await adminResetPassword(resetDoc.user_id, resetPw || undefined);
      ok('Password reset');
      if (result?.temp_password) setGeneratedPw({ email: resetDoc.email, password: result.temp_password });
      setResetDoc(null); setResetPw('');
    } catch (e: any) { err(e?.message || 'Failed to reset password'); }
    finally { setResetSaving(false); }
  };

  const handleAdd = async () => {
    setAddError('');
    if (!addForm.name.trim() || !addForm.email.trim()) { setAddError('Name and email are required'); return; }
    if (addForm.password && addForm.password.length < 6) { setAddError('Password must be at least 6 characters'); return; }
    setAddSaving(true);
    try {
      const created = await adminCreateUser({ ...addForm, password: addForm.password || undefined, role: 'doctor' });
      ok('Doctor added'); setAddOpen(false);
      if (created?.temp_password) setGeneratedPw({ email: addForm.email, password: created.temp_password });
      load();
    } catch (e: any) { setAddError(e?.message || 'Failed to add doctor'); }
    finally { setAddSaving(false); }
  };

  const handleSave = async () => {
    if (!editDoc) return;
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    try {
      await adminUpdateDoctor(editDoc.id, form);
      ok('Doctor updated');
      setModalOpen(false); load();
    } catch (e: any) { setFormError(e?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const SPECIALTIES = ['General Practitioner','General Medicine','Cardiology','Dermatology','Pediatrics','Orthopedics','Gynecology','ENT','Neurology','Psychiatry','Ophthalmology','Other'];

  return (
    <div className="space-y-4">
      <Notify note={note} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{doctors.length} doctor{doctors.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={openAdd}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Doctor
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : doctors.length === 0 ? (
        <div className="py-12 text-center space-y-3">
          <p className="text-sm text-muted-foreground">No doctors added yet. Each doctor gets their own login to write prescriptions.</p>
          <Button size="sm" onClick={openAdd}><Plus className="w-3.5 h-3.5 mr-1" /> Add First Doctor</Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="h-8 border-b border-border bg-muted/40">
                {['Name', 'Specialty', 'Phone', 'Email', ''].map(h => (
                  <th key={h} scope="col" className="px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doctors.map((d) => (
                <tr key={d.id} className="h-10 border-b border-divider last:border-0">
                  <td className="px-3 py-1 font-medium whitespace-nowrap">{d.name}</td>
                  <td className="px-3 py-1 text-muted-foreground">{d.specialty || '—'}</td>
                  <td className="px-3 py-1 text-muted-foreground tabular-nums whitespace-nowrap">{d.phone || '—'}</td>
                  <td className="px-3 py-1 text-muted-foreground">{d.email || '—'}</td>
                  <td className="px-3 py-1 whitespace-nowrap text-right">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit" aria-label={`Edit ${d.name}`} onClick={() => { openEdit(d); setModalOpen(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {d.user_id && d.user_id !== currentUser?.id && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Reset password" aria-label={`Reset password for ${d.name}`} onClick={() => { setResetDoc(d); setResetPw(''); }}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={v => { if (!v) setModalOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Doctor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Specialty">
              <Select value={form.specialty} onValueChange={v => setForm(f => ({ ...f, specialty: v }))}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Field>
            {formError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{formError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Doctor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Name *"><Input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Email (used to log in) *"><Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></Field>
            <Field label="Password (optional)">
              <PasswordInput value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">If left blank, a temporary password is generated and shown once. The doctor must change it on first login.</p>
            </Field>
            {addError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{addError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addSaving}>{addSaving ? 'Adding…' : 'Add Doctor'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetDoc} onOpenChange={v => { if (!v) setResetDoc(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Reset Password — {resetDoc?.name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Field label="New Password (optional)">
              <PasswordInput value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-muted-foreground mt-1">The doctor must change this password on next login.</p>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDoc(null)} disabled={resetSaving}>Cancel</Button>
            <Button onClick={handleReset} disabled={resetSaving || (resetPw.length > 0 && resetPw.length < 6)}>{resetSaving ? 'Resetting…' : 'Reset'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TempPasswordDialog value={generatedPw} onClose={() => setGeneratedPw(null)} />
    </div>
  );
}

// ─── 7. Billing tab (admin) ───────────────────────────────────────────────────

function BillingTab({ clinicId, doctorId }: { clinicId: string; doctorId?: string }) {
  const { note, ok, err } = useNotify();
  const [inv, setInv] = useState({
    prefix: 'INV-',
    tax_rate: '0',
    tax_label: 'GST',
    payment_terms_text: '',
    payment_terms_days: '30',
    footer_text: '',
    currency: 'INR',
    auto_send: false,
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'services' | 'invoice'>('services');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);

  useEffect(() => {
    adminGetInvoiceSettings().then(d => {
      if (d) setInv({
        prefix: d.prefix || 'INV-',
        tax_rate: String(d.tax_rate ?? 0),
        tax_label: d.tax_label || 'GST',
        payment_terms_text: d.payment_terms_text || '',
        payment_terms_days: String(d.payment_terms_days ?? 30),
        footer_text: d.footer_text || '',
        currency: d.currency || 'INR',
        auto_send: !!d.auto_send,
      });
    }).catch(() => {});
  }, []);

  const saveInv = async () => {
    setSaving(true);
    try {
      await adminUpdateInvoiceSettings({
        ...inv,
        tax_rate: parseFloat(inv.tax_rate) || 0,
        payment_terms_days: parseInt(inv.payment_terms_days) || 30,
      });
      ok('Invoice settings saved');
    } catch { err('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAddService = () => {
    setSelectedService(null);
    setShowServiceModal(true);
  };

  const handleCloseServiceModal = () => {
    setShowServiceModal(false);
    setSelectedService(null);
  };

  const handleEditService = (service: any) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const handleServiceSaved = () => {
    setShowServiceModal(false);
    setSelectedService(null);
  };

  return (
    <div className="space-y-4">
      <Notify note={note} />
      {/* Sub-section toggle */}
      <Segmented<'services' | 'invoice'>
        aria-label="Billing settings"
        options={[
          { value: 'services', label: 'Services catalog' },
          { value: 'invoice', label: 'Invoice settings' },
        ]}
        value={activeSection}
        onChange={setActiveSection}
      />

      {activeSection === 'services' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Manage billing services for your clinic</p>
            <Button size="sm" onClick={handleAddService}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Service
            </Button>
          </div>
          <BillingServicesTab
            clinicId={clinicId}
            doctorId={doctorId}
            canManage={true}
            onNotification={(type, msg) => type === 'success' ? ok(msg) : err(msg)}
            showServiceModal={showServiceModal}
            onCloseServiceModal={handleCloseServiceModal}
            selectedService={selectedService}
            onServiceSaved={handleServiceSaved}
            onEditService={handleEditService}
          />
        </div>
      ) : (
        <div className="max-w-md space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Invoice Number Prefix"><Input value={inv.prefix} onChange={e => setInv(f => ({ ...f, prefix: e.target.value }))} placeholder="INV-" /></Field>
            <Field label="Tax Label"><Input value={inv.tax_label} onChange={e => setInv(f => ({ ...f, tax_label: e.target.value }))} placeholder="GST" /></Field>
            <Field label="Default Tax Rate (%)"><Input type="number" value={inv.tax_rate} onChange={e => setInv(f => ({ ...f, tax_rate: e.target.value }))} min="0" max="100" /></Field>
            <Field label="Currency">
              <Select value={inv.currency} onValueChange={v => setInv(f => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="space-y-1.5">
            <Label>Payment Terms</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Days">
                <Input type="number" value={inv.payment_terms_days} onChange={e => setInv(f => ({ ...f, payment_terms_days: e.target.value }))} min="0" max="90" placeholder="30" />
              </Field>
              <Field label="Display Text">
                <Input value={inv.payment_terms_text} onChange={e => setInv(f => ({ ...f, payment_terms_text: e.target.value }))} placeholder="e.g. Due on receipt" />
              </Field>
            </div>
          </div>
          <Field label="Invoice Footer Text">
            <Textarea value={inv.footer_text} onChange={e => setInv(f => ({ ...f, footer_text: e.target.value }))} rows={2} placeholder="Thank you for your visit…" />
          </Field>
          <Row label="Auto-send Invoices" desc="Automatically send invoice emails to customers">
            <Switch checked={inv.auto_send} onCheckedChange={v => setInv(f => ({ ...f, auto_send: v }))} />
          </Row>
          <Button onClick={saveInv} disabled={saving} size="sm">
            {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save Invoice Settings
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── 7. Staff Permissions tab ─────────────────────────────────────────────────

// Module → relevant actions shown in the permission matrix
const MODULE_ACTIONS: Record<string, { key: string; label: string }[]> = {
  patients:      [{ key: 'read', label: 'View' }, { key: 'write', label: 'Edit' }, { key: 'delete', label: 'Delete' }],
  appointments:  [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
  prescriptions: [{ key: 'read', label: 'View' }, { key: 'write', label: 'Create/Edit' }],
  billing:       [{ key: 'read', label: 'View' }, { key: 'write', label: 'Create/Edit' }, { key: 'cancel', label: 'Cancel' }, { key: 'refund', label: 'Refund' }, { key: 'discount', label: 'Discount' }],
  queue:         [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
  ipd:           [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }, { key: 'clinical', label: "Doctor's orders & sign-off" }],
  reports:       [{ key: 'read', label: 'View All' }, { key: 'own_only', label: 'Own Only' }],
  settings:      [{ key: 'read', label: 'View' }, { key: 'write', label: 'Edit' }],
  users:         [{ key: 'read', label: 'View' }, { key: 'write', label: 'Manage' }],
};

const MODULE_LABELS: Record<string, string> = {
  patients: 'Patients', appointments: 'Appointments', prescriptions: 'Prescriptions', billing: 'Billing', queue: 'Queue',
  ipd: 'IPD', reports: 'Reports', settings: 'Settings', users: 'Staff & Users',
};

// Module → plan feature it requires (to hide modules not on this tenant's plan)
// 'patients' is a special sentinel meaning "always available" (matches
// backend permissionService.js's MODULE_PLAN_MAP) — not a real plan feature.
const MODULE_PLAN_FEATURE: Record<string, string> = {
  patients: 'patients', appointments: 'patients', prescriptions: 'patients', billing: 'patients', queue: 'patients', settings: 'patients',
  ipd: 'ipd', reports: 'reports', users: 'role_access',
};

function StaffPermissionsTab() {
  const { currentUser } = useAuth();
  const { isOwner, hasFeature } = usePermission();
  const visibleModules = Object.keys(MODULE_ACTIONS).filter(m => {
    const feature = MODULE_PLAN_FEATURE[m];
    return feature === 'patients' || hasFeature(feature);
  });
  const { note, ok, err } = useNotify();

  const [users, setUsers]       = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [perms, setPerms]       = useState<any | null>(null);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  // Form state for selected user
  const [templateId, setTemplateId] = useState<string>('none');
  const [overrides, setOverrides]   = useState<Record<string, 0 | 1>>({});

  useEffect(() => {
    Promise.all([adminGetUsers(), getPermissionRoleTemplates()])
      .then(([u, t]) => { setUsers(u ?? []); setTemplates(t ?? []); })
      .catch(() => err('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const selectUser = async (u: any) => {
    setSelected(u);
    setPerms(null);
    try {
      const data = await getUserPermissions(u.id);
      setPerms(data);
      setTemplateId(data.role_template_id ?? 'none');
      setOverrides(data.overrides ?? {});
    } catch { err('Failed to load user permissions'); }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateUserPermissions(selected.id, {
        role_template_id: templateId === 'none' ? null : templateId,
        overrides,
      });
      ok('Permissions saved');
    } catch (e: any) { err(e?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6">
      {/* Left: user list (above the editor on phones) */}
      <div className="md:w-56 shrink-0 max-h-60 md:max-h-none rounded-lg border border-border bg-card overflow-y-auto self-start w-full">
        {users.map(u => (
          <button key={u.id} onClick={() => selectUser(u)}
            className={cn(
              'w-full text-left px-3 py-2 border-b border-border last:border-b-0 flex items-start gap-2 transition-colors',
              selected?.id === u.id ? 'bg-primary/10 shadow-[inset_2px_0_0_hsl(var(--primary))]' : 'hover:bg-muted',
            )}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{u.name}</p>
              <StatusChip tone="neutral" className="capitalize">{u.role}</StatusChip>
            </div>
          </button>
        ))}
        {!users.length && <p className="text-xs text-muted-foreground p-3">No staff found</p>}
      </div>

      {/* Right: permission editor */}
      <div className="flex-1 min-w-0">
        <Notify note={note} />
        {!selected && (
          <div className="text-sm text-muted-foreground mt-6">Select a staff member to manage their permissions.</div>
        )}
        {selected && (
          <div className="space-y-5">
            <div>
              <p className="font-medium">{selected.name}</p>
              <p className="text-xs text-muted-foreground">{selected.email}</p>
            </div>

            {/* Role template */}
            <div className="space-y-1.5">
              <Label>Role Template</Label>
              <Select value={templateId} onValueChange={setTemplateId} disabled={!isOwner}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Select template…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Base permissions come from the selected template.</p>
            </div>

            {/* Permission matrix */}
            <div className="space-y-3">
              <Label>Permission Overrides</Label>
              {!isOwner && (
                <p className="text-xs text-highlight-foreground">Only owners can change permission overrides.</p>
              )}
              <p className="text-xs text-muted-foreground">
                Checked items come from the role template. Click a box to override it for this user;
                highlighted boxes are overridden — click "Reset" to fall back to the template default.
              </p>
              <div className="space-y-2">
                {visibleModules.map(module => (
                  <div key={module} className="rounded-lg border border-border bg-card p-3">
                    <p className="text-sm font-medium mb-2">{MODULE_LABELS[module] ?? module}</p>
                    <div className="flex flex-wrap gap-2">
                      {MODULE_ACTIONS[module].map(({ key: action, label }) => {
                        const permKey = `${module}:${action}`;
                        const current = overrides[permKey];
                        const fromTemplate = perms?.effective_permissions?.[module]?.includes(action) ?? false;
                        const checked = current === 1 ? true : current === 0 ? false : fromTemplate;
                        const isOverridden = current !== undefined;
                        return (
                          <div key={permKey} className={cn(
                            'flex items-center gap-1.5 rounded-md border px-2 py-1.5',
                            isOverridden ? 'border-primary/40 bg-primary/5' : 'border-transparent bg-muted/40',
                          )}>
                            <Switch
                              checked={checked}
                              disabled={!isOwner}
                              onCheckedChange={v => setOverrides(prev => ({ ...prev, [permKey]: v ? 1 : 0 }))}
                            />
                            <span className="text-xs">{label}</span>
                            {isOverridden && (
                              <button className="text-2xs text-muted-foreground underline"
                                onClick={() => { const next = { ...overrides }; delete next[permKey]; setOverrides(next); }}
                                disabled={!isOwner}>
                                Reset
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {isOwner && (
              <Button onClick={handleSave} disabled={saving} size="sm">
                {saving ? 'Saving…' : 'Save Permissions'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Section = 'profile' | 'password' | 'preferences' | 'prescription-layout' | 'clinic' | 'hours' | 'staff' | 'doctors' | 'billing' | 'permissions';

const ALL_SECTIONS: { id: Section; label: string; icon: React.ElementType; adminOnly?: boolean; feature?: string; requiresDoctor?: boolean }[] = [
  { id: 'profile',     label: 'Profile',       icon: User,      requiresDoctor: true },
  { id: 'password',    label: 'Password',       icon: Lock },
  { id: 'preferences', label: 'Preferences',    icon: RefreshCw, requiresDoctor: true },
  { id: 'prescription-layout', label: 'Prescription layout', icon: FileEdit, requiresDoctor: true },
  { id: 'clinic',      label: 'Clinic info',    icon: Building2,   adminOnly: true },
  { id: 'hours',       label: 'Clinic hours',   icon: Clock,       adminOnly: true },
  { id: 'staff',       label: 'Staff',          icon: Users,       adminOnly: true, feature: 'role_access' },
  { id: 'doctors',     label: 'Doctors',        icon: Stethoscope, adminOnly: true },
  { id: 'billing',     label: 'Billing',        icon: Receipt,     adminOnly: true },
  { id: 'permissions', label: 'Permissions',    icon: ShieldCheck, adminOnly: true, feature: 'role_access' },
];

export default function SettingsPage() {
  const { currentUser } = useAuth();
  const { hasFeature } = usePermission();
  const isAdmin = currentUser?.role === 'admin';
  // Section lives in the URL (?section=clinic) so other screens can link straight to it
  const [searchParams, setSearchParams] = useSearchParams();
  const section = (searchParams.get('section') as Section) || 'profile';
  const setSection = (id: Section) => setSearchParams({ section: id }, { replace: true });

  useEffect(() => { document.title = 'Settings | OneCheckUp'; }, []);

  const visible = ALL_SECTIONS.filter(s =>
    (!s.adminOnly || isAdmin) && (!s.feature || hasFeature(s.feature)) && (!s.requiresDoctor || !!currentUser?.doctor_id)
  );

  useEffect(() => {
    if (!visible.some(s => s.id === section) && visible.length) setSection(visible[0].id);
  }, [visible.map(s => s.id).join(','), section]);

  const groups = [
    { label: 'My Account', items: visible.filter(s => !s.adminOnly) },
    ...(isAdmin ? [{ label: 'Clinic Admin', items: visible.filter(s => s.adminOnly) }] : []),
  ];

  const current = ALL_SECTIONS.find(s => s.id === section);
  const currentGroup = groups.find(g => g.items.some(i => i.id === section))?.label;

  return (
    <div className="flex flex-col md:flex-row md:h-full">
      {/* Section menu — from md; phones get the picker above the content */}
      <nav aria-label="Settings sections" className="hidden md:flex w-56 shrink-0 border-r border-border bg-card flex-col py-4 overflow-y-auto">
        <p className="px-4 pb-3 text-base font-semibold text-foreground">Settings</p>
        {groups.map(g => (
          <div key={g.label} className="mb-3">
            <p className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground px-4 mb-1">{g.label}</p>
            {g.items.map(item => (
              <button key={item.id} onClick={() => setSection(item.id)}
                aria-current={section === item.id ? 'page' : undefined}
                className={cn(
                  'w-[calc(100%-1rem)] mx-2 flex items-center gap-2.5 h-9 touch:h-10 px-2.5 rounded-lg text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  section === item.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}>
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <main className="flex-1 min-w-0 md:overflow-y-auto px-4 sm:px-6 py-5">
        <div className="max-w-4xl">
          <div className="md:hidden mb-4">
            <Select value={section} onValueChange={v => setSection(v as Section)}>
              <SelectTrigger aria-label="Settings section" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {groups.map(g => (
                  <SelectGroup key={g.label}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map(item => <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <GettingStartedCard compact />
          <div className="mb-5">
            {currentGroup && <p className="hidden md:block text-xs text-muted-foreground">{currentGroup}</p>}
            <h1 className="text-xl font-semibold leading-7 text-foreground">{current?.label}</h1>
          </div>

        {section === 'profile'     && <ProfileTab />}
        {section === 'password'    && <PasswordTab />}
        {section === 'preferences' && <PreferencesTab />}
        {section === 'prescription-layout' && <PrescriptionLayoutTab />}
        {section === 'clinic'      && isAdmin && <ClinicTab />}
        {section === 'hours'       && isAdmin && <ClinicHoursTab />}
        {section === 'staff'       && isAdmin && <StaffTab />}
        {section === 'doctors'     && isAdmin && <DoctorsTab />}
        {section === 'billing'     && isAdmin && currentUser?.clinic_id && (
          <BillingTab clinicId={currentUser.clinic_id} doctorId={currentUser.doctor_id} />
        )}
        {section === 'permissions' && isAdmin && <StaffPermissionsTab />}
        </div>
      </main>
    </div>
  );
}
